import React from 'react';
import { RenderItemParams } from '@atlaskit/tree';
import { Button, CloseIcon, ProviderConsumer as FluentUIThemeConsumer } from '@fluentui/react-northstar';
import i18next from 'i18next';
import ToolbarCSS from '../../../../Toolbar.module.css';
import { ColumnDragSvg, TextSortAscending, TextSortDescending } from '../../../../../../../svg';
import { DEFAULT_SORT_ORDER, EnumSortOrderType } from '../../../../CustomViewContextProvider';
import { NON_SORTABLE_COLUMNS } from '../../../../../../shared/utils/constants';

interface IProps {
  customViewStates: CustomViewStatesType;
  setCustomViewStates: React.Dispatch<React.SetStateAction<CustomViewStatesType>>;
  isDarkMode?: boolean;
}

interface ISortIconProps {
  colorScheme: any;
  itemId: any;
  customViewStates: CustomViewStatesType;
  chooseSortOrder: (type: EnumSortOrderType) => () => void;
}

export const ColumnDragItem = ({ customViewStates, setCustomViewStates, isDarkMode }: IProps) =>
({ item, provided, snapshot }: RenderItemParams) => {
  
  const chooseSortOrder = (type: EnumSortOrderType) => () => {
    setCustomViewStates(
      (prev): CustomViewStatesType => ({
        ...prev,
        columns: {
          ...prev.columns,
          sortOrder: type ? {
            orderBy: item.data.id,
            orderType: type,
          } : null,
        },
      })
    );
  };

  // The same as unticking the column in the list on the left, which is otherwise the only way to take one out.
  const removeColumn = () => {
    setCustomViewStates(
      (prev): CustomViewStatesType => ({
        ...prev,
        columns: {
          ...prev.columns,
          selectedColumns: prev.columns.selectedColumns.filter(col => col.id !== item.data.id),
          sortOrder: prev.columns.sortOrder?.orderBy === item.data.id ? DEFAULT_SORT_ORDER : prev.columns.sortOrder,
        },
      })
    );
  };

  return (
    <FluentUIThemeConsumer
      render={({ siteVariables: { colorScheme } }) => {
        return (
          <div
            ref={provided.innerRef}
            className={`py-2 flex items-center justify-between mb-2.5 rounded-sm ${ToolbarCSS['ColumnItem']}`}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={{
              ...provided.draggableProps.style, height: '40px',
              ...({ backgroundColor: snapshot?.isDragging ? colorScheme.default.background5 : colorScheme.default.background2 })
            }}
          >
            <div className='flex items-center'>
              <span className="mx-2.5">
                <ColumnDragSvg />
              </span>
              <span className="font-semibold text-sm">
                {item.data ? item.data?.label : ''}
              </span>
            </div>
            <div className='flex items-center'>
              {(item.data && !NON_SORTABLE_COLUMNS.includes(item.data.id)) && (
                <div className='flex cursor-pointer align-center'>
                  <SortIcon 
                    colorScheme={colorScheme}
                    customViewStates={customViewStates}
                    itemId={item.data.id}
                    chooseSortOrder={chooseSortOrder}
                  />
                </div>
              )}
              {item.data && (
                <Button
                  text
                  iconOnly
                  size='small'
                  className='mr-2'
                  icon={<CloseIcon />}
                  // Grey at rest like the sort icon, a step darker: its thin strokes read paler than the sort icon's solid letters.
                  // #8A8886 in both themes - borderActive4 is that grey in light but a faint #666666 in dark.
                  styles={{ color: isDarkMode ? colorScheme.default.borderHover : colorScheme.default.borderActive4, ':hover': { color: colorScheme.default.foreground } }}
                  title={i18next.t('ticket.ticket-list.configure-column.remove-column')}
                  aria-label={`${i18next.t('ticket.ticket-list.configure-column.remove-column')} ${item.data.label}`}
                  onClick={removeColumn}
                />
              )}
            </div>
          </div>
        );
      }}
    />
  )
};

const SortIcon = ({ colorScheme, customViewStates, itemId, chooseSortOrder }: ISortIconProps) => {
  const isSelected = customViewStates.columns.sortOrder?.orderBy == itemId;
  return (isSelected && customViewStates.columns.sortOrder?.orderType === EnumSortOrderType.asc) ? <>
    <div className={isSelected && `${ToolbarCSS['SortBtnSelected']}`}
      onClick={chooseSortOrder(EnumSortOrderType.none)}>
      <TextSortAscending fill={colorScheme.brand.foreground} />
    </div>
  </> : <>
    <div className={`${ToolbarCSS[isSelected ? 'SortBtnSelected' : 'SortBtn']}`}
      onClick={chooseSortOrder(isSelected ? EnumSortOrderType.asc : EnumSortOrderType.desc)}>
      <TextSortDescending fill={isSelected ? colorScheme.brand.foreground : colorScheme.default.borderDisabled} />
    </div>
  </>
}